// Remove 'use client' - this is now just data
export interface BaseNavItem {
  id?: string;
  title: string;
  url: string;
  badge?: string;
  icon?: string; // Changed from LucideIcon to string
  isActive?: boolean;
  items?: NavItem[];
  spacingTop?: boolean; // Add extra spacing above this item
  featureFlag?: string;
}

export interface NavItem extends BaseNavItem {
  items?: NavItem[];
}

export interface NavSection extends BaseNavItem {
  items: NavItem[];
}

export interface DynamicSection extends NavSection {
  requiresParam?: boolean;
  paramName?: string;
}

// Flat, ungrouped nav (LF-4815 reverts LF-4796's labelled sections — no
// section header renders). Sections remain a data-grouping convenience: a
// section or item with a `featureFlag` only appears when that flag/toggle is
// ON, and a section whose items are all flag-filtered contributes nothing.
export const navAnchor: NavSection[] = [
  {
    id: 'priorities',
    title: 'Priorities',
    url: '#',
    featureFlag: 'today-upcoming',
    items: [
      {
        id: 'today',
        title: 'Today',
        url: '/today',
        icon: 'StarIcon'
      },
      {
        id: 'upcoming',
        title: 'Upcoming',
        url: '/upcoming',
        icon: 'CalendarIcon'
      },
      {
        id: 'anytime',
        title: 'Anytime',
        url: '/anytime',
        icon: 'InboxIcon'
      }
    ]
  },
  {
    id: 'matters',
    title: 'Matters',
    url: '#',
    items: [
      {
        // Sits above Matters because a lead is pre-matter: the coordinator
        // reads the pipeline top-down, from first enquiry to converted matter.
        // `UserPlusIcon` is registered in `components/ui/icon-mapper.tsx` — an
        // unregistered name renders nothing, silently.
        id: 'leads',
        title: 'Enquiries',
        url: '/enquiries',
        icon: 'UserPlusIcon',
        featureFlag: 'leads'
      },
      {
        id: 'all',
        title: 'Matters',
        url: '/all',
        icon: 'FolderIcon',
        isActive: true
      },
      {
        id: 'safe-custody',
        title: 'Safe Custody',
        url: '/safe-custody',
        icon: 'FolderLockIcon',
        featureFlag: 'safe-custody'
      }
    ]
  },
  {
    id: 'ai-tools',
    title: 'AI Tools',
    url: '#',
    items: [
      {
        id: 'scribe',
        title: 'Scribe',
        url: '/scribe',
        icon: 'AudioLinesIcon'
      },
      {
        id: 'intake',
        title: 'Intake',
        url: '/intake',
        icon: 'ClipboardListIcon'
      }
    ]
  },
  {
    id: 'library',
    title: 'Library',
    url: '#',
    items: [
      {
        id: 'contacts',
        title: 'Contacts',
        url: '/contacts',
        icon: 'SquareUserIcon',
        featureFlag: 'contacts-page',
        // Presentation hint honoured by the compact icon rail to set the Library
        // group off with a gap; the full main sidebar ignores it. Kept in the
        // canonical data so the rail needs no second list of its own (VALE-1971).
        spacingTop: true
      },
      {
        id: 'templates',
        title: 'Templates',
        url: '/templates',
        icon: 'FileStackIcon'
      }
    ]
  }
];

/**
 * The single flatten + feature-gate for the primary navigation. Both surfaces
 * that render it — the full main sidebar (`MainSidebarContent`) and the compact
 * icon rail (`IconRail`) — derive their visible items from this one function over
 * `navAnchor`, so the two presentations can no longer drift out of step
 * (VALE-1971). The gate is `!flag || features.includes(flag)`: a section or item
 * with no flag always shows, and a section whose items are all filtered out
 * contributes nothing. Sections are a data-grouping convenience with no header of
 * their own (LF-4815).
 */
export function getVisibleNavItems(
  sections: NavSection[],
  features: string[]
): NavItem[] {
  const isFlagOn = (flag?: string) => !flag || features.includes(flag);
  return (sections || []).flatMap((section) =>
    isFlagOn(section.featureFlag)
      ? (section.items || []).filter((item) => isFlagOn(item.featureFlag))
      : []
  );
}

export const navSecondary: NavItem[] = [];

/**
 * Navigation item type definition
 */
export interface NavSubItem {
  /** Display title */
  title: string;
  /** Navigation URL */
  url: string;
}

/**
 * User data type definition
 */
export interface UserData {
  /** User's full name */
  name: string;
  /** User's email address */
  email: string;
}

/**
 * Complete navigation data structure
 */
export interface NavigationData {
  /** Current user information */
  user: UserData | null;
  /** Whether user data is loading */
  isLoadingUser?: boolean;
  /** Fixed anchor navigation items (top) */
  navAnchor: NavSection[];
  /** Fixed secondary navigation items (bottom) */
  navSecondary: NavItem[];
}

/**
 * Default user data - null when not loaded
 */
export const defaultUserData: UserData | null = null;

/**
 * Homepage navigation data
 */
export const homepageNavigationData: NavigationData = {
  user: defaultUserData,
  navAnchor: navAnchor,
  navSecondary: navSecondary,
  isLoadingUser: true
};

/**
 * Default navigation data (alias for homepage)
 */
export const navigationData = homepageNavigationData;
